/**
 * ============================================================================
 * Material 模块 API 工厂函数
 * ============================================================================
 *
 * 📋 文件职责：
 * - 定义 Material（物料管理）模块的所有 API 请求方法
 * - 通过工厂模式实现请求客户端的依赖注入
 * - 提供完整的 TypeScript 类型安全
 *
 * 🎯 核心概念：工厂模式
 * - 工厂函数：createMaterialApi(client) - 接收请求客户端，返回 API 方法集合
 * - 依赖注入：不依赖具体的请求实现（axios、uni.request 等）
 * - 平台无关：Web 端和 App 端都可以使用，只需提供不同的适配器
 *
 * 💡 设计理念：
 * - "给我一个能发送请求的工具，我就能为你生成所有 API 方法"
 * - 请求客户端是"工具"，API 方法是"产品"，工厂函数是"生产线"
 *
 * 📖 使用方式：
 *
 * **Web 端（Vben Admin + axios）**：
 * ```typescript
 * // 1. 创建适配器（让 Vben 的 requestClient 符合 IRequestClient 接口）
 * import { requestClient } from '#/api/request'
 * import type { IRequestClient } from '@bayes-mes/shared/api/core/client'
 *
 * const webAdapter: IRequestClient = {
 *   get: (url, config) => requestClient.get(url, config).then(res => res.data),
 *   post: (url, data, config) => requestClient.post(url, data, config).then(res => res.data),
 *   put: (url, data, config) => requestClient.put(url, data, config).then(res => res.data),
 *   delete: (url, config) => requestClient.delete(url, config).then(res => res.data),
 * }
 *
 * // 2. 调用工厂函数，传入适配器
 * import { createMaterialApi } from '@bayes-mes/shared/api/material/factory'
 * export const materialApi = createMaterialApi(webAdapter)
 *
 * // 3. 使用生成的 API
 * const result = await materialApi.getMaterialList({ page: 1 })
 * ```
 *
 * **App 端（uni-app X）**：
 * ```typescript
 * // 1. 创建适配器（让 uni.request 符合 IRequestClient 接口）
 * const uniAdapter: IRequestClient = {
 *   get: (url, config) => new Promise((resolve, reject) => {
 *     uni.request({
 *       url,
 *       method: 'GET',
 *       data: config?.params,
 *       success: (res) => resolve(res.data.data),
 *       fail: reject
 *     })
 *   }),
 *   // ... 其他方法
 * }
 *
 * // 2. 调用工厂函数
 * import { createMaterialApi } from '@bayes-mes/shared/api/material/factory'
 * export const materialApi = createMaterialApi(uniAdapter)
 * ```
 *
 * ⚠️ 注意事项：
 * - **请求客户端接口**：所有工厂函数都使用 `api/core/client.ts` 中定义的 `IRequestClient` 接口
 * - **不要重复定义接口**：从 `../core/client` 导入，不要在每个模块中重新定义
 * - **适配器职责**：适配器负责将平台特定的请求客户端转换为符合 `IRequestClient` 的对象
 * - **响应数据解包**：适配器需要处理响应数据的解包（如 `res.data.data`）
 * - **类型安全**：所有方法都有完整的 TypeScript 类型定义
 * - **注释详细**：每个方法都有详细的中文注释，包括使用场景、参数、返回值、数据流向
 *
 * 🔄 更新时机：
 * - 后端新增 API 方法
 * - 后端修改参数或响应结构
 * - 后端修改路由路径
 *
 * ✅ 验证方式：
 * - 确保所有 Controller 的 public 方法都已映射
 * - 确保所有类型导入正确
 * - 确保 URL 路径与后端路由一致
 * - 运行 `npm run type-check` 验证 TypeScript 编译
 *
 * @module api/material/factory
 * @description Material（物料管理）模块的 API 工厂函数
 *
 * @source 对应后端文件:
 * - backend/routes/api.php (路由定义)
 * - backend/app/Modules/Material/Controllers/MaterialController.php (控制器)
 *
 * @generated AI-assisted
 * @author AI Assistant
 * @created 2025-10-18
 * @last-updated 2025-10-18
 *
 * @change-log
 * - 2025-10-18: 初始创建，生成工厂函数
 *
 * @notes
 * - API 路径前缀: /api/v1/materials
 * - 使用工厂模式实现平台无关的 API 调用
 * - 支持完整的 TypeScript 类型推断
 * - IRequestClient 接口定义在 api/core/client.ts
 */

// ============================================================================
// 类型导入
// ============================================================================

import type {
  Material,
  MaterialUnit,
  MaterialQueryParams,
  CreateMaterialForm,
  UpdateMaterialForm,
  CreateMaterialUnitForm,
  ConvertUnitForm,
  BatchImportMaterialItem,
  MaterialGlobalStatistics,
  MaterialStatistics,
  MaterialBatchConversionPreview,
  MaterialBatchConversionResult,
  ConvertMaterialToBatchForm,
  UnitConversionResult,
} from '../../types/material'

import type {
  PaginationResult,
  BatchImportResult,                // 批量导入结果（通用类型）
} from '../../types/api'

// ============================================================================
// 导入统一的请求客户端接口（⚠️ 重要）
// ============================================================================
// 不要重复定义接口！所有工厂函数共享同一个接口
import type { IRequestClient } from '../core/client'

// ============================================================================
// 导入 API 路由定义
// ============================================================================
import { API_ROUTES } from '../routes'

// ============================================================================
// Material API 工厂函数
// ============================================================================

/**
 * 创建 Material 模块的 API 方法集合
 *
 * @description
 * 这是一个工厂函数，接收一个请求客户端，返回一组 API 方法。
 *
 * **工厂模式的核心思想**：
 * - 输入：一个能发送 HTTP 请求的客户端（符合 IRequestClient 接口）
 * - 输出：一组封装好的 API 方法（具有完整类型安全）
 * - 好处：同一套 API 定义，可以在不同平台使用（Web、App）
 *
 * **为什么不直接写死 axios 或 uni.request？**
 * - 因为 Shared 库是跨平台的，不能依赖特定平台的请求库
 * - 通过依赖注入，让使用者提供自己的请求客户端
 * - 这样 Web 端可以用 axios，App 端可以用 uni.request
 *
 * @param client 请求客户端（必须符合 IRequestClient 接口）
 * @returns Material 模块的所有 API 方法
 *
 * @example
 * // Web 端使用
 * import { createMaterialApi } from '@bayes-mes/shared/api/material/factory'
 * import { webAdapter } from '@/api/adapters/http'
 *
 * export const materialApi = createMaterialApi(webAdapter)
 *
 * // 调用 API
 * const materials = await materialApi.getMaterialList({ page: 1 })
 *
 * @example
 * // App 端使用
 * import { createMaterialApi } from '@bayes-mes/shared/api/material/factory'
 * import { uniAdapter } from '@/api/adapters/http'
 *
 * export const materialApi = createMaterialApi(uniAdapter)
 */
export function createMaterialApi(client: IRequestClient) {
  return {
    // ========================================================================
    // 物料 CRUD 操作
    // ========================================================================

    /**
     * 获取物料列表（分页）
     *
     * @description
     * 获取物料列表，支持分页、排序和多条件筛选。
     *
     * **使用场景**：
     * - 物料管理页面的列表展示
     * - 物料选择器的数据源
     * - 统计报表的数据查询
     *
     * **数据流向**：
     * ```
     * 前端 → GET /api/v1/materials?page=1&per_page=20
     *      → MaterialController::index()
     *      → MaterialService::list()
     *      → 返回分页数据（Material[]）
     * ```
     *
     * @param params 查询参数（所有字段可选）
     * @param params.page 页码（默认1）
     * @param params.per_page 每页数量（默认20）
     * @param params.keyword 关键字搜索（物料编码、名称、规格）
     * @param params.material_type 物料类型筛选（RAW/SEMI/PRODUCT）
     * @param params.category 分类筛选
     * @param params.status 状态筛选（1-启用 0-停用）
     * @param params.enable_batch 是否启用批次管理
     * @param params.enable_multi_unit 是否启用多单位
     * @param params.sort_field 排序字段
     * @param params.sort_order 排序方式（asc/desc）
     *
     * @returns 分页的物料列表
     *
     * @example
     * // 基础查询
     * const result = await materialApi.getMaterialList({ page: 1 })
     *
     * @example
     * // 带筛选条件
     * const result = await materialApi.getMaterialList({
     *   page: 1,
     *   per_page: 20,
     *   keyword: 'M001',
     *   material_type: MaterialType.RAW,
     *   status: MaterialStatus.ACTIVE
     * })
     */
    getMaterialList(params?: MaterialQueryParams) {
      return client.get<PaginationResult<Material>>(API_ROUTES.material.list, {
        params,
      })
    },

    /**
     * 获取物料详情
     *
     * @description
     * 根据物料ID获取物料的详细信息，包括关联的单位列表。
     *
     * **使用场景**：
     * - 物料详情页面
     * - 物料编辑表单的数据回显
     * - BOM配置时查看物料信息
     *
     * **数据流向**：
     * ```
     * 前端 → GET /api/v1/materials/{id}
     *      → MaterialController::show()
     *      → MaterialService::getById()
     *      → 返回物料详情（Material，含 units 关联）
     * ```
     *
     * @param id 物料ID
     * @returns 物料详情（包含单位列表）
     *
     * @example
     * const material = await materialApi.getMaterialDetail(1)
     * console.log(material.materialCode, material.units)
     */
    getMaterialDetail(id: number) {
      return client.get<Material>(API_ROUTES.material.detail(id))
    },

    /**
     * 创建物料
     *
     * @description
     * 创建新的物料记录。物料编码必须唯一。
     * 如果启用多单位管理，会自动创建基本单位记录。
     *
     * **使用场景**：
     * - 物料管理页面的新增功能
     * - 批量导入时的单条创建
     *
     * **数据流向**：
     * ```
     * 前端 → POST /api/v1/materials
     *      → CreateMaterialRequest::rules() (验证)
     *      → MaterialController::store()
     *      → MaterialService::create()
     *      → 返回创建的物料（Material）
     * ```
     *
     * **注意事项**：
     * - material_code、material_name、material_type、base_unit 为必填字段
     * - material_code 必须唯一
     * - enable_multi_unit=true 时会自动创建基本单位记录
     *
     * @param data 物料表单数据
     * @returns 创建的物料信息
     *
     * @example
     * const material = await materialApi.createMaterial({
     *   material_code: 'M001',
     *   material_name: '原材料A',
     *   material_type: MaterialType.RAW,
     *   base_unit: 'KG',
     *   enable_batch: true,
     *   enable_multi_unit: true,
     *   min_stock: 100,
     *   status: MaterialStatus.ACTIVE
     * })
     */
    createMaterial(data: CreateMaterialForm) {
      return client.post<Material>(API_ROUTES.material.create, data)
    },

    /**
     * 更新物料
     *
     * @description
     * 更新物料信息。所有字段都是可选的（部分更新）。
     * 物料编码如果修改，必须保证唯一性。
     *
     * **使用场景**：
     * - 物料编辑功能
     * - 批量修改物料状态
     * - 调整库存参数
     *
     * **数据流向**：
     * ```
     * 前端 → PUT /api/v1/materials/{id}
     *      → UpdateMaterialRequest::rules() (验证)
     *      → MaterialController::update()
     *      → MaterialService::update()
     *      → 返回更新后的物料（Material）
     * ```
     *
     * **注意事项**：
     * - 所有字段都是可选的，只更新提供的字段
     * - material_code 如果修改，必须保证唯一性
     * - 会记录审计日志
     *
     * @param id 物料ID
     * @param data 更新数据（所有字段可选）
     * @returns 更新后的物料信息
     *
     * @example
     * const material = await materialApi.updateMaterial(1, {
     *   material_name: '原材料A（修改）',
     *   min_stock: 200,
     *   status: MaterialStatus.INACTIVE
     * })
     */
    updateMaterial(id: number, data: UpdateMaterialForm) {
      return client.put<Material>(API_ROUTES.material.materials(id), data)
    },

    /**
     * 删除物料（软删除）
     *
     * @description
     * 软删除物料记录。如果物料正在被BOM、库存、工单等使用，删除会失败。
     *
     * **使用场景**：
     * - 物料管理页面的删除功能
     * - 批量删除操作
     *
     * **数据流向**：
     * ```
     * 前端 → DELETE /api/v1/materials/{id}
     *      → MaterialController::destroy()
     *      → MaterialService::delete()
     *      → 软删除（设置 deleted_at）
     * ```
     *
     * **注意事项**：
     * - 这是软删除，数据不会真正删除
     * - 如果物料正在使用，会返回错误
     * - 会记录审计日志
     *
     * @param id 物料ID
     *
     * @example
     * await materialApi.deleteMaterial(1)
     */
    deleteMaterial(id: number) {
      return client.delete(API_ROUTES.material.delete(id))
    },

    /**
     * 批量删除物料
     *
     * @description
     * 批量删除多个物料记录。每个物料独立删除，失败不影响其他物料。
     * 返回详细的删除结果，包括成功数、失败数和错误信息。
     *
     * **使用场景**：
     * - 物料管理页面的批量删除功能
     * - 清理无用物料
     * - 数据维护
     *
     * **数据流向**：
     * ```
     * 前端 → POST /api/v1/materials/batch-delete
     *      → MaterialController::batchDestroy()
     *      → MaterialService::batchDelete()
     *      → 逐个调用 delete()
     *      → 返回删除结果
     * ```
     *
     * **注意事项**：
     * - 这是软删除，数据不会真正删除
     * - 每个物料独立验证，部分失败不会影响其他物料
     * - 会记录审计日志
     * - 返回详细的成功和失败信息
     *
     * @param ids 物料ID数组
     * @returns 删除结果（成功数、失败数、错误列表）
     *
     * @example
     * const result = await materialApi.batchDeleteMaterials([1, 2, 3])
     * console.log(`成功删除 ${result.deleted} 条，失败 ${result.failed} 条`)
     * result.errors.forEach(error => {
     *   console.log(`错误: ${error}`)
     * })
     */
    batchDeleteMaterials(ids: number[]) {
      return client.post<{
        deleted: number
        failed: number
        errors: string[]
        total: number
      }>(API_ROUTES.material.batchDelete, { ids })
    },

    // ========================================================================
    // 物料统计信息
    // ========================================================================

    /**
     * 获取物料全局统计信息
     *
     * @description
     * 获取系统中所有物料的统计数据，包括：
     * - 物料总数、启用数、停用数
     * - 按分类统计
     * - 按物料类型统计
     * - 有单位/无单位物料数量
     *
     * **使用场景**：
     * - Dashboard 首页展示
     * - 物料管理页面的统计面板
     * - 数据报表
     *
     * **数据流向**：
     * ```
     * 前端 → GET /api/v1/materials/statistics
     *      → MaterialController::statistics()
     *      → MaterialService::getGlobalStatistics()
     *      → 返回全局统计（MaterialGlobalStatistics）
     * ```
     *
     * @returns 全局统计信息
     *
     * @example
     * const stats = await materialApi.getMaterialGlobalStatistics()
     * console.log('物料总数:', stats.totalMaterials)
     * console.log('按类型:', stats.byMaterialType)
     */
    getMaterialGlobalStatistics() {
      return client.get<MaterialGlobalStatistics>(API_ROUTES.material.statistics)
    },

    /**
     * 获取单个物料统计信息
     *
     * @description
     * 获取指定物料的详细统计数据，包括：
     * - 基本信息
     * - 单位数量
     * - 被引用的BOM数量
     * - 当前库存和锁定库存
     *
     * **使用场景**：
     * - 物料详情页面的统计面板
     * - 物料分析报表
     *
     * **数据流向**：
     * ```
     * 前端 → GET /api/v1/materials/{id}/statistics
     *      → MaterialController::materialStatistics()
     *      → MaterialService::getMaterialStatistics()
     *      → 返回物料统计（MaterialStatistics）
     * ```
     *
     * @param id 物料ID
     * @returns 物料统计信息
     *
     * @example
     * const stats = await materialApi.getMaterialStatistics(1)
     * console.log('当前库存:', stats.currentStock)
     * console.log('被引用的BOM数:', stats.inBomsCount)
     */
    getMaterialStatistics(id: number) {
      return client.get<MaterialStatistics>(API_ROUTES.material.materialsStatistics(id))
    },

    /**
     * 获取非批次物料转批次管理预检结果
     */
    getBatchConversionPreview(id: number) {
      return client.get<MaterialBatchConversionPreview>(API_ROUTES.material.batchConversionPreview(id))
    },

    /**
     * 将非批次管理物料转换为批次管理物料
     */
    enableBatchConversion(id: number, data: ConvertMaterialToBatchForm) {
      return client.post<MaterialBatchConversionResult>(API_ROUTES.material.enableBatchConversion(id), data)
    },

    // ========================================================================
    // 物料单位管理
    // ========================================================================

    /**
     * 获取物料的单位列表
     *
     * @description
     * 获取指定物料的所有单位记录，包括基本单位、采购单位、生产单位等。
     * 只有启用了多单位管理的物料才有多个单位。
     *
     * **使用场景**：
     * - 物料单位管理页面
     * - 单位选择器的数据源
     * - BOM配置时选择单位
     *
     * **数据流向**：
     * ```
     * 前端 → GET /api/v1/materials/{id}/units
     *      → MaterialController::getUnits()
     *      → MaterialService::getUnits()
     *      → UnitConversionService::getUnitConversions()
     *      → 返回单位列表（MaterialUnit[]）
     * ```
     *
     * @param id 物料ID
     * @returns 物料单位列表
     *
     * @example
     * const units = await materialApi.getMaterialUnits(1)
     * units.forEach(unit => {
     *   console.log(unit.unitCode, unit.conversionRate)
     * })
     */
    getMaterialUnits(id: number) {
      return client.get<MaterialUnit[]>(API_ROUTES.material.units(id))
    },

    /**
     * 添加物料单位
     *
     * @description
     * 为物料添加新的单位。前提条件：
     * - 物料必须启用多单位管理
     * - 单位代码不能重复
     * - 基本单位只能有一个
     *
     * **使用场景**：
     * - 物料单位管理的新增功能
     * - 初始化物料的单位配置
     *
     * **数据流向**：
     * ```
     * 前端 → POST /api/v1/materials/{id}/units
     *      → CreateMaterialUnitRequest::rules() (验证)
     *      → MaterialController::addUnit()
     *      → MaterialService::addUnit()
     *      → 返回创建的单位（MaterialUnit）
     * ```
     *
     * **注意事项**：
     * - unit_code、unit_name、unit_type、conversion_rate 为必填字段
     * - unit_code 在同一物料下必须唯一
     * - 基本单位（TYPE_BASE）只能有一个
     * - 转换率相对于基本单位（基本单位的转换率为1）
     *
     * @param id 物料ID
     * @param data 单位数据
     * @returns 创建的单位信息
     *
     * @example
     * const unit = await materialApi.addMaterialUnit(1, {
     *   unit_code: 'BOX',
     *   unit_name: '箱',
     *   unit_type: MaterialUnitType.PURCHASE,
     *   conversion_rate: 100, // 1箱 = 100个
     *   is_packaging: true,
     *   package_quantity: 100,
     *   status: 1
     * })
     */
    addMaterialUnit(id: number, data: CreateMaterialUnitForm) {
      return client.post<MaterialUnit>(API_ROUTES.material.addUnit(id), data)
    },

    /**
     * 更新物料单位
     *
     * @description
     * 更新单位信息。不能修改基本单位的类型。
     *
     * **使用场景**：
     * - 单位信息的修改
     * - 调整转换率
     * - 更新单位价格
     *
     * **数据流向**：
     * ```
     * 前端 → PUT /api/v1/materials/{id}/units/{unitId}
     *      → MaterialController::updateUnit()
     *      → MaterialService::updateUnit()
     *      → 返回更新后的单位（MaterialUnit）
     * ```
     *
     * **注意事项**：
     * - 不能修改基本单位的类型
     * - 单位代码如果修改，必须保证唯一性
     * - 会记录审计日志
     *
     * @param materialId 物料ID
     * @param unitId 单位ID
     * @param data 更新数据
     * @returns 更新后的单位信息
     *
     * @example
     * const unit = await materialApi.updateMaterialUnit(1, 5, {
     *   conversion_rate: 120,  // 修改转换率
     *   price: 1200
     * })
     */
    updateMaterialUnit(_materialId: number, unitId: number, data: Partial<CreateMaterialUnitForm>) {
      return client.put<MaterialUnit>(
        API_ROUTES.material.materialUnits(unitId), // 注意：只需unitId
        data,
      )
    },

    /**
     * 删除物料单位
     *
     * @description
     * 删除单位记录。注意：
     * - 不能删除基本单位
     * - 如果单位正在被BOM、采购订单等使用，删除会失败
     *
     * **使用场景**：
     * - 单位管理的删除功能
     * - 清理无用的单位
     *
     * **数据流向**：
     * ```
     * 前端 → DELETE /api/v1/materials/{id}/units/{unitId}
     *      → MaterialController::deleteUnit()
     *      → MaterialService::deleteUnit()
     *      → 删除单位记录
     * ```
     *
     * **注意事项**：
     * - 不能删除基本单位（TYPE_BASE）
     * - 如果单位正在使用，会返回错误
     * - 会记录审计日志
     *
     * @param materialId 物料ID
     * @param unitId 单位ID
     *
     * @example
     * await materialApi.deleteMaterialUnit(1, 5)
     */
    deleteMaterialUnit(_materialId: number, unitId: number) {
      return client.delete(API_ROUTES.material.materialUnits(unitId)) // 注意：routes中是/material-units/{id}
    },

    // ========================================================================
    // 单位转换计算
    // ========================================================================

    /**
     * 单位转换计算
     *
     * @description
     * 计算不同单位之间的数量转换。
     * 例如：100个 → ? 箱，如果1箱=100个，则结果为1箱。
     *
     * **使用场景**：
     * - BOM配置时的单位转换
     * - 采购订单的单位换算
     * - 生产计划的数量计算
     * - 库存管理的单位转换
     *
     * **数据流向**：
     * ```
     * 前端 → POST /api/v1/materials/convert-unit
     *      → MaterialController::convertUnit() (验证)
     *      → MaterialService::convertUnit()
     *      → UnitConversionService::convert()
     *      → 返回转换结果（UnitConversionResult）
     * ```
     *
     * **计算原理**：
     * - 所有单位都相对于基本单位有一个转换率
     * - 转换公式：目标数量 = 源数量 × 源转换率 ÷ 目标转换率
     * - 例如：100个（转换率1）→ 箱（转换率100）= 100 × 1 ÷ 100 = 1箱
     *
     * @param data 转换参数
     * @param data.material_id 物料ID
     * @param data.quantity 源数量
     * @param data.from_unit 源单位
     * @param data.to_unit 目标单位
     * @returns 转换结果（包含转换率和目标数量）
     *
     * @example
     * const result = await materialApi.convertMaterialUnit({
     *   material_id: 1,
     *   quantity: 100,
     *   from_unit: 'PCS',
     *   to_unit: 'BOX'
     * })
     * console.log(`${result.fromQuantity} ${result.fromUnit} = ${result.toQuantity} ${result.toUnit}`)
     * // 输出：100 PCS = 1 BOX
     */
    convertMaterialUnit(data: ConvertUnitForm) {
      return client.post<UnitConversionResult>(API_ROUTES.material.convertUnit, data)
    },

    // ========================================================================
    // 批量操作
    // ========================================================================

    /**
     * 批量导入物料
     *
     * @description
     * 批量导入物料数据。返回成功和失败的记录。
     *
     * **使用场景**：
     * - Excel 导入物料
     * - 批量创建物料
     * - 数据迁移
     *
     * **数据流向**：
     * ```
     * 前端 → POST /api/v1/materials/batch-import
     *      → MaterialController::batchImport()
     *      → MaterialService::batchImport()
     *      → 逐条调用 create()
     *      → 返回导入结果（BatchImportResult）
     * ```
     *
     * **注意事项**：
     * - 每条记录独立验证，失败不影响其他记录
     * - 返回详细的成功和失败信息
     * - 使用事务确保数据一致性
     *
     * @param materials 物料数据数组
     * @returns 导入结果（成功数、失败数、详细信息）
     *
     * @example
     * const result = await materialApi.batchImportMaterials([
     *   {
     *     material_code: 'M001',
     *     material_name: '原材料A',
     *     material_type: MaterialType.RAW,
     *     base_unit: 'KG'
     *   },
     *   {
     *     material_code: 'M002',
     *     material_name: '原材料B',
     *     material_type: MaterialType.RAW,
     *     base_unit: 'KG'
     *   }
     * ])
     * console.log(`成功导入 ${result.successCount} 条，失败 ${result.failedCount} 条`)
     * result.failedItems.forEach(item => {
     *   console.log(`第 ${item.row} 行失败: ${item.error}`)
     * })
     */
    batchImportMaterials(materials: BatchImportMaterialItem[]) {
      return client.post<BatchImportResult>(API_ROUTES.material.import, {
        materials,
      })
    },

    /**
     * 导出物料数据（Excel）
     *
     * @description
     * 根据查询条件导出物料数据到 Excel 文件。
     *
     * **使用场景**：
     * - 导出物料列表
     * - 生成物料报表
     * - 数据备份
     *
     * **数据流向**：
     * ```
     * 前端 → POST /api/v1/materials/export
     *      → MaterialController::export()
     *      → 生成 Excel 文件
     *      → 返回文件流（Blob）
     * ```
     *
     * **注意事项**：
     * - 返回类型是 Blob（二进制文件）
     * - 需要设置 responseType: 'blob'
     * - 前端需要处理文件下载
     *
     * @param params 查询参数（与列表查询相同）
     * @returns Excel 文件（Blob）
     *
     * @example
     * const blob = await materialApi.exportMaterials({
     *   material_type: MaterialType.RAW,
     *   status: MaterialStatus.ACTIVE
     * })
     * // 下载文件
     * const url = URL.createObjectURL(blob)
     * const a = document.createElement('a')
     * a.href = url
     * a.download = '物料数据.xlsx'
     * a.click()
     * URL.revokeObjectURL(url)
     */
    exportMaterials(params?: MaterialQueryParams) {
      return client.post(API_ROUTES.material.export, params, {
        responseType: 'blob',
      })
    },

    // ========================================================================
    // 扩展功能（v3.14.0 新增）
    // ========================================================================

    /**
     * 通过物料编码查找物料
     *
     * @description
     * 根据物料编码快速查找物料详情。
     *
     * **使用场景**：
     * - 扫码查询物料
     * - 通过编码快速搜索
     * - API集成时根据编码获取物料
     * - 物料选择器的编码搜索
     *
     * **数据流向**：
     * ```
     * 前端 → GET /api/v1/materials/code/{code}
     *      → MaterialController::findByCode()
     *      → MaterialService::findByCode()
     *      → 查询数据库（material_code字段）
     *      → 返回物料详情（Material，含 units 关联）
     * ```
     *
     * **注意事项**：
     * - 物料编码必须完全匹配（区分大小写）
     * - 如果不存在，返回 404 错误
     * - 返回数据包含单位列表
     *
     * @param code 物料编码（如 'M001'）
     * @returns 物料详情（包含单位列表）
     *
     * @example
     * ```typescript
     * // 通过编码查询物料
     * const material = await materialApi.getMaterialByCode('M001')
     * console.log(material.materialName, material.units)
     *
     * // 扫码查询场景
     * async function handleScan(code: string) {
     *   try {
     *     const material = await materialApi.getMaterialByCode(code)
     *     router.push(`/material/${material.id}`)  // 跳转到详情页
     *   } catch (error) {
     *     message.error('物料不存在：' + code)
     *   }
     * }
     *
     * // 在搜索框中使用
     * async function handleSearch(code: string) {
     *   if (!code) return
     *
     *   try {
     *     const material = await materialApi.getMaterialByCode(code)
     *     // 显示物料详情
     *     showMaterialDetail(material)
     *   } catch (error) {
     *     // 编码不存在，使用列表搜索
     *     loadList({ keyword: code })
     *   }
     * }
     * ```
     */
    getMaterialByCode(code: string) {
      return client.get<Material>(API_ROUTES.material.findByCode(code))
    },

    /**
     * 获取物料分类列表
     *
     * @description
     * 获取系统中所有物料分类的列表（去重并排序）。
     *
     * **使用场景**：
     * - 物料分类选择器的数据源
     * - 分类筛选下拉框
     * - 分类统计分析
     * - 物料分类管理
     *
     * **数据流向**：
     * ```
     * 前端 → GET /api/v1/materials/categories
     *      → MaterialController::getCategories()
     *      → MaterialService::getCategories()
     *      → 查询数据库（DISTINCT category）
     *      → 返回分类数组（字符串数组）
     * ```
     *
     * **注意事项**：
     * - 只返回非空的分类
     * - 自动去重和排序
     * - 如果没有任何分类，返回空数组 []
     *
     * @returns 分类列表（字符串数组，已排序）
     *
     * @example
     * ```typescript
     * // 获取所有分类
     * const categories = await materialApi.getMaterialCategories()
     * console.log(categories)  // ['原材料', '半成品', '成品', '辅料', '包材']
     *
     * // 用于下拉选择器
     * const categoryOptions = categories.map(cat => ({
     *   label: cat,
     *   value: cat
     * }))
     *
     * // 用于筛选
     * <Select>
     *   {categories.map(cat => (
     *     <Option key={cat} value={cat}>{cat}</Option>
     *   ))}
     * </Select>
     *
     * // 分类统计
     * async function loadCategoryStats() {
     *   const categories = await materialApi.getMaterialCategories()
     *
     *   for (const cat of categories) {
     *     const list = await materialApi.getMaterialList({ category: cat })
     *     console.log(`${cat}: ${list.total}个物料`)
     *   }
     * }
     * ```
     */
    getMaterialCategories() {
      return client.get<string[]>(API_ROUTES.material.categories)
    },
  }
}

// ============================================================================
// 类型导出（供类型推断使用）
// ============================================================================

/**
 * Material API 实例类型
 *
 * @description
 * 通过 ReturnType 工具类型自动推断出 API 实例的类型。
 * 这样在使用时可以获得完整的类型提示。
 */
export type MaterialApi = ReturnType<typeof createMaterialApi>
